import { useState, useMemo, useCallback, createContext, useContext, useRef, useEffect } from "react";
import { cn, getColorById, timeToMinutes, minutesToTime, checkShiftOverlap, employeeHasShiftOnDate, getCurrencySymbol, calculateShiftCost, calculateTotalHours, calculateBillableHours, calculateTotalBillableHours, getSalaryDailyCost, isPastDate, calculatePaidAbsenceDailyCost, getEffectiveHoursForDate } from "../lib/utils";
import ShiftBlock from "./ShiftBlock";
import ShiftEditDialog from "./ShiftEditDialog";
import BreakRequiredDialog from "./BreakRequiredDialog";
import CopySingleShiftDialog from "./CopySingleShiftDialog";
import DeleteShiftConfirmDialog from "./DeleteShiftConfirmDialog";
import EmployeeDetailsPopover from "./EmployeeDetailsPopover";
import EventsRow from "./EventsRow";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";
import { toast } from "sonner";
import { Plus, Lock, CalendarClock } from "lucide-react";
import { Badge } from "./ui/badge";

// Context to share drag guide positions
const DragGuideContext = createContext(null);
export const useDragGuide = () => useContext(DragGuideContext);

const DayView = ({ 
  date, 
  employees, 
  shifts, 
  absences = [],
  events = [],
  businessHours, 
  hoursOverrides = [],
  breakTypes = [],
  shiftPatterns = [],
  snapInterval = 15,
  allowMultipleShifts = true,
  shiftGapMinutes = 0,
  currency = "GBP",
  rowHeight = 48,
  showStats = true,
  showCosts = true,
  scrollable = false,
  onCreateShift, 
  onUpdateShift, 
  onDeleteShift 
}) => {
  const [editingShift, setEditingShift] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dragGuides, setDragGuides] = useState(null); // { startPercent, endPercent }
  const [clickedStartTime, setClickedStartTime] = useState(null); // Time where user clicked
  const [clickedEndTime, setClickedEndTime] = useState(null); // Time where user released drag
  const [hoverInfo, setHoverInfo] = useState(null); // { employeeId, startPercent, endPercent, time }
  const lastDragEndTimeRef = useRef(0); // Track when last drag ended
  
  // Break required dialog state
  const [isBreakDialogOpen, setIsBreakDialogOpen] = useState(false);
  const [breakDialogData, setBreakDialogData] = useState(null); // { employee, startTime, endTime, requiredBreaks }
  
  // Copy shift dialog state
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [copyShiftData, setCopyShiftData] = useState(null); // { shift, employee }
  
  // Delete confirmation dialog state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deleteShiftData, setDeleteShiftData] = useState(null); // { shift, employee }
  
  // Drag-to-create state
  const [dragCreateState, setDragCreateState] = useState(null); // { employeeId, startTime, startPercent, currentTime, currentPercent }
  const isDraggingRef = useRef(false); // Track if currently dragging (for preventing click)

  // Check if this date is in the past (locked)
  const isLocked = useMemo(() => isPastDate(date), [date]);

  // Get day's business hours (with override support)
  const dateStr = date.toISOString().split('T')[0];
  const dayHours = useMemo(() => {
    return getEffectiveHoursForDate(dateStr, businessHours, hoursOverrides);
  }, [dateStr, businessHours, hoursOverrides]);

  // Check if there's an active override for today
  const activeOverride = dayHours?.override || null;

  // Helper to check if an employee is absent on this date
  const getEmployeeAbsence = useCallback((employeeId) => {
    return absences.find(a => 
      a.employee_id === employeeId && 
      a.start_date <= dateStr && 
      a.end_date >= dateStr
    );
  }, [absences, dateStr]);

  // Get absence reason label
  const getAbsenceLabel = (absence) => {
    if (!absence) return "";
    if (absence.reason_type === "custom") return absence.custom_reason;
    const labels = {
      holiday: "Holiday",
      sick: "Sick",
      maternity: "Maternity",
      paternity: "Paternity",
      bereavement: "Bereavement",
      training: "Training"
    };
    return labels[absence.reason_type] || absence.reason_type;
  };

  // Snap time to interval
  const snapToInterval = (minutes) => {
    return Math.round(minutes / snapInterval) * snapInterval;
  };

  // Generate hour markers
  const hourMarkers = useMemo(() => {
    if (!dayHours || !dayHours.is_open) return [];
    
    const startHour = parseInt(dayHours.open_time.split(':')[0]);
    const endHour = parseInt(dayHours.close_time.split(':')[0]);
    const markers = [];
    
    for (let h = startHour; h <= endHour; h++) {
      markers.push(h);
    }
    return markers;
  }, [dayHours]);

  // Generate tick marks (15min and 30min intervals)
  const tickMarks = useMemo(() => {
    if (!dayHours || !dayHours.is_open) return [];
    
    const startMinutes = timeToMinutes(dayHours.open_time);
    const endMinutes = timeToMinutes(dayHours.close_time);
    const totalMinutes = endMinutes - startMinutes;
    const ticks = [];
    
    // Generate ticks every 15 minutes
    for (let m = startMinutes; m <= endMinutes; m += 15) {
      const percent = ((m - startMinutes) / totalMinutes) * 100;
      const minuteOfHour = m % 60;
      
      // Determine tick type
      let type = 'small'; // 15min tick
      if (minuteOfHour === 30) {
        type = 'medium'; // 30min tick
      } else if (minuteOfHour === 0) {
        type = 'hour'; // Hour tick (already shown as label)
      }
      
      ticks.push({ percent, type, minute: m });
    }
    
    return ticks;
  }, [dayHours]);

  const totalHours = hourMarkers.length > 0 ? hourMarkers.length - 1 : 0;
  
  // Fixed pixel width for scrollable mode (100px per hour)
  const pixelsPerHour = 100;
  const fixedTimelineWidth = totalHours * pixelsPerHour;

  // Handle drag guide updates from ShiftBlock
  const handleDragGuideUpdate = useCallback((guides) => {
    setDragGuides(guides);
    // When drag ends (guides become null), record the time
    if (guides === null) {
      lastDragEndTimeRef.current = Date.now();
    }
  }, []);

  // Calculate time from mouse position on timeline
  const getTimeFromPosition = useCallback((e, trackElement) => {
    if (!dayHours.is_open || !trackElement) return null;
    
    const rect = trackElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    
    const openMins = timeToMinutes(dayHours.open_time);
    const closeMins = timeToMinutes(dayHours.close_time);
    const totalMins = closeMins - openMins;
    
    const rawMinutes = openMins + (percent * totalMins);
    const snappedMinutes = snapToInterval(rawMinutes);
    
    // Clamp to business hours
    const clampedMinutes = Math.max(openMins, Math.min(closeMins - snapInterval, snappedMinutes));
    
    return minutesToTime(clampedMinutes);
  }, [dayHours, snapInterval, snapToInterval]);

  // Calculate both time and percent from mouse position
  const getTimeAndPercentFromPosition = useCallback((e, trackElement) => {
    if (!dayHours || !dayHours.is_open || !trackElement) return null;
    
    const rect = trackElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentRaw = Math.max(0, Math.min(1, x / rect.width));
    
    const openMins = timeToMinutes(dayHours.open_time);
    const closeMins = timeToMinutes(dayHours.close_time);
    const totalMins = closeMins - openMins;
    
    const rawMinutes = openMins + (percentRaw * totalMins);
    const snappedMinutes = snapToInterval(rawMinutes);
    
    // Clamp to business hours
    const clampedMinutes = Math.max(openMins, Math.min(closeMins, snappedMinutes));
    const time = minutesToTime(clampedMinutes);
    const percent = ((clampedMinutes - openMins) / totalMins) * 100;
    
    return { time, percent };
  }, [dayHours, snapInterval, snapToInterval]);

  // Drag-to-create handlers
  const handleDragCreateStart = useCallback((e, employee, trackElement) => {
    // Don't allow creating shifts on locked (past) dates
    if (isLocked) return;
    
    // Check if employee is absent
    const absence = getEmployeeAbsence(employee.id);
    if (absence) return;
    
    if (!dayHours || !dayHours.is_open) return;
    
    // Don't start drag if clicking on an existing shift block
    if (e.target.closest('[data-testid^="shift-block-"]') || 
        e.target.closest('[data-testid^="shift-handle-"]') ||
        e.target.closest('[data-testid^="absence-block-"]')) {
      return;
    }
    
    const posInfo = getTimeAndPercentFromPosition(e, trackElement);
    if (!posInfo) return;
    
    isDraggingRef.current = false; // Will be set true on actual drag movement
    setDragCreateState({
      employeeId: employee.id,
      employee: employee,
      startTime: posInfo.time,
      startPercent: posInfo.percent,
      currentTime: posInfo.time,
      currentPercent: posInfo.percent,
      trackElement: trackElement
    });
    
    // Clear hover info when starting drag
    setHoverInfo(null);
  }, [dayHours, isLocked, getEmployeeAbsence, getTimeAndPercentFromPosition]);

  const handleDragCreateMove = useCallback((e) => {
    if (!dragCreateState) return;
    
    const posInfo = getTimeAndPercentFromPosition(e, dragCreateState.trackElement);
    if (!posInfo) return;
    
    // Mark as dragging if moved more than a small threshold
    if (!isDraggingRef.current) {
      const percentDiff = Math.abs(posInfo.percent - dragCreateState.startPercent);
      if (percentDiff > 1) { // ~1% movement threshold
        isDraggingRef.current = true;
      }
    }
    
    setDragCreateState(prev => ({
      ...prev,
      currentTime: posInfo.time,
      currentPercent: posInfo.percent
    }));
  }, [dragCreateState, getTimeAndPercentFromPosition]);

  const handleDragCreateEnd = useCallback((e) => {
    if (!dragCreateState) return;
    
    const wasDragging = isDraggingRef.current;
    isDraggingRef.current = false;
    
    // Only if we actually dragged, record the time to prevent accidental click
    if (wasDragging) {
      lastDragEndTimeRef.current = Date.now();
    }
    
    // Only create shift if we actually dragged (not just clicked)
    if (wasDragging) {
      const employee = dragCreateState.employee;
      
      // Check if employee already has a shift (if multiple shifts disabled)
      if (!allowMultipleShifts) {
        const existingShift = employeeHasShiftOnDate(shifts, employee.id, dateStr);
        if (existingShift) {
          toast.error("Employee already has a shift on this day. Edit or delete the existing shift.");
          setDragCreateState(null);
          return;
        }
      }
      
      // Determine start and end times (handle dragging in either direction)
      const startMins = timeToMinutes(dragCreateState.startTime);
      const endMins = timeToMinutes(dragCreateState.currentTime);
      
      let finalStartTime, finalEndTime;
      if (startMins <= endMins) {
        finalStartTime = dragCreateState.startTime;
        finalEndTime = dragCreateState.currentTime;
      } else {
        finalStartTime = dragCreateState.currentTime;
        finalEndTime = dragCreateState.startTime;
      }
      
      // Ensure minimum shift duration (at least one snap interval)
      const startMinsFinal = timeToMinutes(finalStartTime);
      let endMinsFinal = timeToMinutes(finalEndTime);
      if (endMinsFinal - startMinsFinal < snapInterval) {
        endMinsFinal = startMinsFinal + snapInterval;
        finalEndTime = minutesToTime(endMinsFinal);
      }
      
      // Check for overlap with existing shifts
      const overlap = checkShiftOverlap(
        shifts,
        employee.id,
        dateStr,
        finalStartTime,
        finalEndTime,
        null,
        shiftGapMinutes
      );
      
      if (overlap) {
        const gapText = shiftGapMinutes > 0 
          ? `Shifts must be at least ${shiftGapMinutes} minutes apart`
          : "Shift would overlap with an existing shift";
        toast.error(gapText);
        setDragCreateState(null);
        return;
      }
      
      // Calculate shift duration in hours
      const shiftDurationHours = (endMinsFinal - startMinsFinal) / 60;
      
      // Check if any required breaks apply to this shift duration
      const requiredBreaks = breakTypes.filter(bt => 
        bt.is_required && shiftDurationHours > bt.min_shift_hours
      );
      
      if (requiredBreaks.length > 0) {
        // Has required breaks - open simple break dialog
        setBreakDialogData({
          employee: employee,
          startTime: finalStartTime,
          endTime: finalEndTime,
          requiredBreaks: requiredBreaks
        });
        setIsBreakDialogOpen(true);
      } else {
        // No required breaks - create shift directly
        onCreateShift({
          employee_id: employee.id,
          date: dateStr,
          start_time: finalStartTime,
          end_time: finalEndTime,
        });
      }
    }
    
    setDragCreateState(null);
  }, [dragCreateState, allowMultipleShifts, shifts, dateStr, snapInterval, shiftGapMinutes, breakTypes, onCreateShift]);

  // Global mouse move/up handlers for drag-to-create
  useEffect(() => {
    if (!dragCreateState) return;
    
    const handleMouseMove = (e) => handleDragCreateMove(e);
    const handleMouseUp = (e) => handleDragCreateEnd(e);
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragCreateState, handleDragCreateMove, handleDragCreateEnd]);

  // Handle mouse move on timeline for hover effect
  const handleTimelineMouseMove = useCallback((e, employee, trackElement) => {
    // Don't show hover on locked (past) dates
    if (isLocked || !dayHours.is_open) {
      setHoverInfo(null);
      return;
    }
    
    // Don't show hover if over a shift block
    if (e.target.closest('[data-testid^="shift-block-"]')) {
      setHoverInfo(null);
      return;
    }
    
    const time = getTimeFromPosition(e, trackElement);
    if (!time) {
      setHoverInfo(null);
      return;
    }
    
    const openMins = timeToMinutes(dayHours.open_time);
    const closeMins = timeToMinutes(dayHours.close_time);
    const totalMins = closeMins - openMins;
    const timeMins = timeToMinutes(time);
    
    const startPercent = ((timeMins - openMins) / totalMins) * 100;
    const endPercent = ((timeMins + snapInterval - openMins) / totalMins) * 100;
    
    setHoverInfo({
      employeeId: employee.id,
      startPercent,
      endPercent,
      time
    });
  }, [dayHours, getTimeFromPosition, snapInterval, isLocked]);

  const handleTimelineMouseLeave = useCallback(() => {
    setHoverInfo(null);
  }, []);

  // Handle click on timeline to create shift
  const handleTimelineClick = useCallback((e, employee, trackElement) => {
    // Don't allow creating shifts on locked (past) dates
    if (isLocked) {
      toast.error("Past rotas are locked and cannot be modified.");
      return;
    }
    
    // Check if employee is absent
    const absence = getEmployeeAbsence(employee.id);
    if (absence) {
      toast.error(`${employee.name} is on ${getAbsenceLabel(absence)} on this date.`);
      return;
    }
    
    if (!dayHours.is_open) return;
    
    // Don't create new shift if a drag just ended (prevents accidental popup after drag)
    const timeSinceLastDrag = Date.now() - lastDragEndTimeRef.current;
    if (timeSinceLastDrag < 300) {
      return;
    }
    
    // Don't create new shift if clicking on an existing shift block
    if (e.target.closest('[data-testid^="shift-block-"]') || 
        e.target.closest('[data-testid^="shift-handle-"]') ||
        e.target.closest('[data-testid^="absence-block-"]')) {
      return;
    }

    // Check if employee already has a shift (if multiple shifts disabled)
    if (!allowMultipleShifts) {
      const existingShift = employeeHasShiftOnDate(shifts, employee.id, dateStr);
      if (existingShift) {
        toast.error("Employee already has a shift on this day. Edit or delete the existing shift.");
        return;
      }
    }
    
    // Calculate the clicked time
    const clickedTime = getTimeFromPosition(e, trackElement);
    setClickedStartTime(clickedTime);
    
    setEditingShift(null);
    setEditingEmployee(employee);
    setIsDialogOpen(true);
    setHoverInfo(null);
  }, [dayHours, allowMultipleShifts, shifts, dateStr, getTimeFromPosition, isLocked, getEmployeeAbsence]);

  const handleShiftClick = useCallback((shift, employee) => {
    // Allow viewing shifts on locked dates but mark as read-only
    setEditingShift(shift);
    setEditingEmployee(employee);
    setIsDialogOpen(true);
  }, []);

  const handleSaveShift = useCallback((times) => {
    const gapText = shiftGapMinutes > 0 
      ? `Shifts must be at least ${shiftGapMinutes} minutes apart`
      : "Shift would overlap with an existing shift";
    
    if (editingShift) {
      // Check for overlap when updating
      const overlap = checkShiftOverlap(
        shifts, 
        editingShift.employee_id, 
        editingShift.date, 
        times.start_time, 
        times.end_time, 
        editingShift.id,
        shiftGapMinutes
      );
      
      if (overlap) {
        toast.error(gapText);
        return;
      }
      
      onUpdateShift(editingShift.id, times);
    } else if (editingEmployee) {
      // Check for overlap when creating
      const overlap = checkShiftOverlap(
        shifts, 
        editingEmployee.id, 
        dateStr, 
        times.start_time, 
        times.end_time,
        null,
        shiftGapMinutes
      );
      
      if (overlap) {
        toast.error(gapText);
        return;
      }
      
      onCreateShift({
        employee_id: editingEmployee.id,
        date: dateStr,
        ...times,
      });
    }
    setIsDialogOpen(false);
  }, [dateStr, editingShift, editingEmployee, onCreateShift, onUpdateShift, shifts, shiftGapMinutes]);

  const handleDeleteShift = useCallback(() => {
    if (editingShift) {
      onDeleteShift(editingShift.id);
    }
    setIsDialogOpen(false);
  }, [editingShift, onDeleteShift]);

  // Handle break dialog confirmation (for drag-to-create shifts with required breaks)
  const handleBreakDialogConfirm = useCallback((data) => {
    if (!breakDialogData) return;
    
    onCreateShift({
      employee_id: breakDialogData.employee.id,
      date: dateStr,
      start_time: data.start_time,
      end_time: data.end_time,
      breaks: data.breaks,
    });
    
    setIsBreakDialogOpen(false);
    setBreakDialogData(null);
  }, [breakDialogData, dateStr, onCreateShift]);

  // Context menu handlers
  const handleContextEdit = useCallback((shift, employee) => {
    setEditingShift(shift);
    setEditingEmployee(employee);
    setClickedStartTime(null);
    setClickedEndTime(null);
    setIsDialogOpen(true);
  }, []);

  const handleContextCopy = useCallback((shift, employee) => {
    setCopyShiftData({ shift, employee });
    setIsCopyDialogOpen(true);
  }, []);

  const handleContextDelete = useCallback((shift, employee) => {
    setDeleteShiftData({ shift, employee });
    setIsDeleteDialogOpen(true);
  }, []);

  // Handle copy shift
  const handleCopyShift = useCallback(async ({ shift, targetEmployeeId, targetDate }) => {
    try {
      await onCreateShift({
        employee_id: targetEmployeeId,
        date: targetDate,
        start_time: shift.start_time,
        end_time: shift.end_time,
        breaks: shift.breaks || [],
        rate_name: shift.rate_name,
        rate_segments: shift.rate_segments || [],
      });
      toast.success("Shift copied successfully");
    } catch (error) {
      console.error("Failed to copy shift:", error);
      toast.error("Failed to copy shift");
      throw error;
    }
  }, [onCreateShift]);

  // Handle delete shift confirmation
  const handleDeleteShiftConfirm = useCallback((shift) => {
    onDeleteShift(shift.id);
    toast.success("Shift deleted");
  }, [onDeleteShift]);

  // Handle shift update from drag/resize with overlap check
  const handleShiftUpdate = useCallback((shiftId, updates) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return;
    
    const newStart = updates.start_time || shift.start_time;
    const newEnd = updates.end_time || shift.end_time;
    
    // Check for overlap with gap
    const overlap = checkShiftOverlap(
      shifts, 
      shift.employee_id, 
      shift.date, 
      newStart, 
      newEnd, 
      shiftId,
      shiftGapMinutes
    );
    
    if (overlap) {
      const gapText = shiftGapMinutes > 0 
        ? `Shifts must be at least ${shiftGapMinutes} minutes apart`
        : "Cannot overlap with existing shift";
      toast.error(gapText);
      return;
    }
    
    onUpdateShift(shiftId, updates);
  }, [shifts, onUpdateShift, shiftGapMinutes]);

  const dayShifts = shifts.filter(s => s.date === dateStr);
  
  // Filter events for the current day
  const dayEvents = useMemo(() => {
    return events.filter(e => e.date === dateStr);
  }, [events, dateStr]);

  if (!dayHours || !dayHours.is_open) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground" data-testid="day-closed-message">
        <div className="text-center">
          {activeOverride ? (
            <>
              <CalendarClock className="w-8 h-8 mx-auto mb-2 text-primary" />
              <p className="text-lg font-medium">{activeOverride.name}</p>
              <p className="text-sm">Closed - No shifts available</p>
            </>
          ) : (
            <>
              <p className="text-lg font-medium">Closed</p>
              <p className="text-sm">No shifts available for this day</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <DragGuideContext.Provider value={{ dragGuides, setDragGuides: handleDragGuideUpdate }}>
      <div data-testid="day-view">
        {scrollable ? (
          <div className="relative flex">
            {/* Scrollable area for employee names and timeline */}
            <div className={cn("flex-1 overflow-hidden", showStats && "mr-24")}>
              <ScrollArea className="w-full">
                <div style={{ minWidth: `${fixedTimelineWidth + 192 + 48}px` }}>
                  {/* Time Ruler with Ticks - Scrollable */}
                  <div className="flex border-b border-border bg-muted/30">
                    <div className="w-48 flex-shrink-0 px-2 border-r border-border sticky left-0 bg-card z-20 flex items-center h-14 shadow-[2px_0_4px_rgba(0,0,0,0.05)]">
                      <span className="text-sm font-medium text-muted-foreground">Employee</span>
                    </div>
                    {/* Add left and right padding so hour markers are visible */}
                    <div className="px-6 flex-shrink-0">
                      <div className="relative h-14 z-0" style={{ width: `${fixedTimelineWidth}px` }}>
                        {/* Use tickMarks to position hour labels for perfect alignment */}
                        {tickMarks.filter(t => t.type === 'hour').map((tick) => {
                          const hour = Math.floor(tick.minute / 60);
                          return (
                            <div
                              key={tick.minute}
                              className="absolute top-1 flex flex-col items-center"
                              style={{ left: `${tick.percent}%`, transform: 'translateX(-50%)' }}
                            >
                              <span className="text-xs tabular-nums text-muted-foreground">
                                {hour.toString().padStart(2, '0')}:00
                              </span>
                            </div>
                          );
                        })}
                        {tickMarks.map((tick, index) => (
                          <div
                            key={index}
                            className={cn(
                              "absolute bottom-0 w-px",
                              tick.type === 'hour' && "h-4 bg-foreground/40",
                              tick.type === 'medium' && "h-3 bg-foreground/25",
                              tick.type === 'small' && "h-2 bg-foreground/15"
                            )}
                            style={{ left: `${tick.percent}%`, transform: 'translateX(-50%)' }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Events Row - Above employee rows (scrollable mode) */}
                  {dayEvents.length > 0 && (
                    <EventsRow
                      events={dayEvents}
                      timelineStart={dayHours.open_time}
                      timelineEnd={dayHours.close_time}
                      rowHeight={32}
                      scrollable={true}
                      tickMarks={tickMarks}
                    />
                  )}

                  {/* Employee Rows - Scrollable */}
                  {employees.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-muted-foreground">
                      <p>No employees added yet. Go to Employees tab to add staff.</p>
                    </div>
                  ) : (
                    employees.map((employee) => {
                      const color = getColorById(employee.color);
                      const employeeShifts = dayShifts.filter(s => s.employee_id === employee.id);
                      const absence = getEmployeeAbsence(employee.id);

                      return (
                        <div key={employee.id} className="flex border-b border-border hover:bg-accent/20 transition-colors" style={{ height: `${rowHeight}px` }}>
                          <EmployeeDetailsPopover employee={employee} currency={currency}>
                            <div 
                              className="w-48 flex-shrink-0 px-2 border-r border-border bg-card sticky left-0 z-20 flex items-center shadow-[2px_0_4px_rgba(0,0,0,0.05)] cursor-pointer hover:bg-accent/30 transition-colors"
                              data-testid={`employee-info-${employee.id}`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                {employee.avatar_url ? (
                                  <img 
                                    src={employee.avatar_url} 
                                    alt={employee.name}
                                    className={cn(
                                      "rounded-full object-cover flex-shrink-0 border border-border",
                                      rowHeight <= 48 ? "w-7 h-7" : "w-10 h-10"
                                    )}
                                  />
                                ) : (
                                  <div 
                                    className={cn(
                                      "rounded-full flex items-center justify-center font-semibold flex-shrink-0",
                                      rowHeight <= 48 ? "w-7 h-7 text-xs" : "w-10 h-10 text-sm",
                                      !color.isCustom && color.avatar
                                    )}
                                    style={color.isCustom ? { backgroundColor: employee.color + '20', color: employee.color } : {}}
                                  >
                                    {employee.name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <div className="overflow-hidden min-w-0">
                                  <p className="font-medium text-sm truncate leading-tight">{employee.name}</p>
                                  {rowHeight > 44 && (
                                    <p className="text-xs text-muted-foreground truncate leading-tight">{employee.role}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </EmployeeDetailsPopover>
                          {/* Add left and right padding to match header */}
                          <div className="px-6 flex-shrink-0 group">
                            <div 
                              className={cn(
                                "relative z-0 select-none",
                                absence ? "cursor-not-allowed" : "cursor-crosshair"
                              )}
                              style={{ width: `${fixedTimelineWidth}px`, height: `${rowHeight}px` }}
                              onMouseDown={(e) => !absence && handleDragCreateStart(e, employee, e.currentTarget)}
                              onClick={(e) => handleTimelineClick(e, employee, e.currentTarget)}
                              onMouseMove={(e) => !absence && !dragCreateState && handleTimelineMouseMove(e, employee, e.currentTarget)}
                              onMouseLeave={handleTimelineMouseLeave}
                              data-testid={`timeline-track-${employee.id}`}
                            >
                              {tickMarks.map((tick, index) => (
                                <div
                                  key={index}
                                  className={cn(
                                    "absolute top-0 bottom-0 pointer-events-none",
                                    tick.type === 'hour' && "w-px bg-border/40",
                                    tick.type === 'medium' && "w-px bg-border/20",
                                    tick.type === 'small' && "w-px bg-border/10"
                                  )}
                                  style={{ left: `${tick.percent}%` }}
                                />
                              ))}
                              {/* Drag-to-create preview for scrollable mode */}
                              {dragCreateState && dragCreateState.employeeId === employee.id && (
                                (() => {
                                  const startP = Math.min(dragCreateState.startPercent, dragCreateState.currentPercent);
                                  const endP = Math.max(dragCreateState.startPercent, dragCreateState.currentPercent);
                                  const width = endP - startP;
                                  const startMins = timeToMinutes(dragCreateState.startTime);
                                  const endMins = timeToMinutes(dragCreateState.currentTime);
                                  const displayStartTime = startMins <= endMins ? dragCreateState.startTime : dragCreateState.currentTime;
                                  const displayEndTime = startMins <= endMins ? dragCreateState.currentTime : dragCreateState.startTime;
                                  
                                  return (
                                    <div 
                                      className={cn(
                                        "absolute top-1 bottom-1 pointer-events-none rounded-md border-2 border-dashed",
                                        getColorById(employee.color).bg,
                                        getColorById(employee.color).border,
                                        "opacity-80"
                                      )}
                                      style={{ 
                                        left: `${startP}%`, 
                                        width: `${width}%`,
                                        minWidth: '2px'
                                      }}
                                      data-testid="drag-create-preview"
                                    >
                                      {width > 5 && (
                                        <div className="absolute inset-0 flex items-center justify-center text-xs font-medium truncate px-1">
                                          {displayStartTime} - {displayEndTime}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()
                              )}
                              {dragGuides && (
                                <>
                                  <div 
                                    className="absolute top-0 bottom-0 w-0.5 bg-primary/60 pointer-events-none z-10"
                                    style={{ left: `${dragGuides.startPercent}%`, transform: 'translateX(-50%)' }}
                                  />
                                  <div 
                                    className="absolute top-0 bottom-0 w-0.5 bg-primary/60 pointer-events-none z-10"
                                    style={{ left: `${dragGuides.endPercent}%`, transform: 'translateX(-50%)' }}
                                  />
                                </>
                              )}
                              {/* Absence Block - covers entire timeline when absent */}
                              {absence && (
                                <div 
                                  className="absolute inset-0 bg-muted/80 border border-dashed border-muted-foreground/30 rounded flex items-center justify-center z-5"
                                  data-testid={`absence-block-${employee.id}`}
                                >
                                  <div className="text-center">
                                    <p className="text-sm font-medium text-muted-foreground">
                                      {getAbsenceLabel(absence)}
                                    </p>
                                    {absence.is_paid && (
                                      <span className="text-xs text-green-600 dark:text-green-400">(Paid)</span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {/* Show hint when no shifts and not absent */}
                              {employeeShifts.length === 0 && !absence && !dragCreateState && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="flex items-center gap-1 text-muted-foreground/50">
                                    <Plus className="w-3 h-3" />
                                    <span className="text-xs">Click or drag to add shift</span>
                                  </div>
                                </div>
                              )}
                              {employeeShifts.map((shift) => (
                                <ShiftBlock
                                  key={shift.id}
                                  shift={shift}
                                  employee={employee}
                                  employeeShifts={employeeShifts}
                                  breakTypes={breakTypes}
                                  timelineStart={dayHours.open_time}
                                  timelineEnd={dayHours.close_time}
                                  snapInterval={snapInterval}
                                  shiftGapMinutes={shiftGapMinutes}
                                  scrollable={true}
                                  isLocked={isLocked}
                                  onUpdate={(updates) => handleShiftUpdate(shift.id, updates)}
                                  onClick={() => handleShiftClick(shift, employee)}
                                  onContextEdit={handleContextEdit}
                                  onContextCopy={handleContextCopy}
                                  onContextDelete={handleContextDelete}
                                  onDragGuideUpdate={handleDragGuideUpdate}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <ScrollBar orientation="horizontal" />
              </ScrollArea>
            </div>

            {/* Fixed Stats Column - Outside ScrollArea */}
            {showStats && (
              <div className="w-24 flex-shrink-0 border-l border-border absolute right-0 top-0 bottom-0 bg-background z-20">
                {/* Stats Header */}
                <div className="h-14 border-b border-border bg-muted/50 flex items-center justify-center">
                  <span className="text-sm font-medium text-muted-foreground">Stats</span>
                </div>
                {/* Stats Rows */}
                {employees.map((employee) => {
                  const employeeShifts = dayShifts.filter(s => s.employee_id === employee.id);
                  const currencySymbol = getCurrencySymbol(currency);
                  const dailyHours = calculateTotalBillableHours(employeeShifts, breakTypes);
                  const absence = getEmployeeAbsence(employee.id);
                  
                  // Calculate daily cost
                  const isSalaryEmployee = employee.pay_type === "salary";
                  const isOnUnpaidLeave = absence && !absence.is_paid;
                  let dailyCost = 0;
                  let costType = null; // 'shift', 'salary', 'paid_leave', or null
                  
                  if (isOnUnpaidLeave) {
                    // Employee is on unpaid leave - no cost
                    dailyCost = 0;
                    costType = null;
                  } else if (absence && absence.is_paid) {
                    // Employee is on paid leave - calculate absence cost
                    dailyCost = calculatePaidAbsenceDailyCost(absence, employee);
                    costType = 'paid_leave';
                  } else if (isSalaryEmployee) {
                    // Salary employee - always show daily salary cost (even without shifts)
                    dailyCost = getSalaryDailyCost(employee);
                    costType = 'salary';
                  } else if (employeeShifts.length > 0) {
                    // Hourly employee with shifts - calculate shift costs
                    dailyCost = employeeShifts.reduce((acc, shift) => {
                      return acc + calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, employee);
                    }, 0);
                    costType = 'shift';
                  }
                  
                  const hasCost = dailyCost > 0;

                  return (
                    <div 
                      key={employee.id} 
                      className="border-b border-border bg-muted/30 flex items-center justify-center"
                      style={{ height: `${rowHeight}px` }}
                    >
                      {employeeShifts.length > 0 ? (
                        <div className="space-y-0.5 text-center">
                          <span className="text-xs tabular-nums font-medium">{dailyHours.toFixed(2)}h</span>
                          {showCosts && hasCost && (
                            <p className="text-xs tabular-nums font-medium text-primary">{currencySymbol}{dailyCost.toFixed(2)}</p>
                          )}
                        </div>
                      ) : showCosts && costType === 'paid_leave' ? (
                        <div className="space-y-0.5 text-center">
                          <span className="text-xs tabular-nums text-muted-foreground">(Paid)</span>
                          <p className="text-xs tabular-nums font-medium text-green-600 dark:text-green-400">{currencySymbol}{dailyCost.toFixed(2)}</p>
                        </div>
                      ) : showCosts && costType === 'salary' ? (
                        <div className="space-y-0.5 text-center">
                          <span className="text-xs tabular-nums text-muted-foreground">(Salary)</span>
                          <p className="text-xs tabular-nums font-medium text-primary">{currencySymbol}{dailyCost.toFixed(2)}</p>
                        </div>
                      ) : isOnUnpaidLeave ? (
                        <span className="text-xs text-muted-foreground">(Unpaid)</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
            <div className="w-full">
              {/* Time Ruler with Ticks - Fit to screen */}
              <div className="flex border-b border-border bg-muted/30">
                <div className="w-48 flex-shrink-0 px-2 border-r border-border flex items-center">
                  <span className="text-sm font-medium text-muted-foreground">Employee</span>
                </div>
                {/* Add left and right padding for neater appearance */}
                <div className="flex-1 relative h-14 mx-6">
                  {/* Use tickMarks to position hour labels for perfect alignment */}
                  {tickMarks.filter(t => t.type === 'hour').map((tick) => {
                    const hour = Math.floor(tick.minute / 60);
                    return (
                      <div
                        key={tick.minute}
                        className="absolute top-1 flex flex-col items-center"
                        style={{ left: `${tick.percent}%`, transform: 'translateX(-50%)' }}
                      >
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {hour.toString().padStart(2, '0')}:00
                        </span>
                      </div>
                    );
                  })}
                  {tickMarks.map((tick, index) => (
                    <div
                      key={index}
                      className={cn(
                        "absolute bottom-0 w-px",
                        tick.type === 'hour' && "h-4 bg-foreground/40",
                        tick.type === 'medium' && "h-3 bg-foreground/25",
                        tick.type === 'small' && "h-2 bg-foreground/15"
                      )}
                      style={{ left: `${tick.percent}%`, transform: 'translateX(-50%)' }}
                    />
                  ))}
                </div>
                {/* Stats column placeholder to match row structure */}
                {showStats && (
                  <div className="w-24 flex-shrink-0 border-l border-border text-center bg-muted/50 flex items-center justify-center">
                    <span className="text-sm font-medium text-muted-foreground">Stats</span>
                  </div>
                )}
              </div>

              {/* Events Row - Above employee rows (fit-to-screen mode) */}
              {dayEvents.length > 0 && (
                <EventsRow
                  events={dayEvents}
                  timelineStart={dayHours.open_time}
                  timelineEnd={dayHours.close_time}
                  rowHeight={32}
                  scrollable={false}
                  tickMarks={tickMarks}
                />
              )}

              {/* Employee Rows - Fit to screen */}
              {employees.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-muted-foreground">
                  <p>No employees added yet. Go to Employees tab to add staff.</p>
                </div>
              ) : (
                employees.map((employee) => {
                  const color = getColorById(employee.color);
                  const employeeShifts = dayShifts.filter(s => s.employee_id === employee.id);
                  const absence = getEmployeeAbsence(employee.id);
                  const currencySymbol = getCurrencySymbol(currency);
                  const dailyHours = calculateTotalBillableHours(employeeShifts, breakTypes);
                  
                  // Calculate daily cost
                  const isSalaryEmployee = employee.pay_type === "salary";
                  const isOnUnpaidLeave = absence && !absence.is_paid;
                  let dailyCost = 0;
                  let costType = null; // 'shift', 'salary', 'paid_leave', or null
                  
                  if (isOnUnpaidLeave) {
                    // Employee is on unpaid leave - no cost
                    dailyCost = 0;
                    costType = null;
                  } else if (absence && absence.is_paid) {
                    // Employee is on paid leave - calculate absence cost
                    dailyCost = calculatePaidAbsenceDailyCost(absence, employee);
                    costType = 'paid_leave';
                  } else if (isSalaryEmployee) {
                    // Salary employee - always show daily salary cost (even without shifts)
                    dailyCost = getSalaryDailyCost(employee);
                    costType = 'salary';
                  } else if (employeeShifts.length > 0) {
                    // Hourly employee with shifts - calculate shift costs
                    dailyCost = employeeShifts.reduce((acc, shift) => {
                      return acc + calculateShiftCost(shift.start_time, shift.end_time, 0, shift, breakTypes, employee);
                    }, 0);
                    costType = 'shift';
                  }
                  
                  const hasCost = dailyCost > 0;

                  return (
                    <div key={employee.id} className="flex border-b border-border hover:bg-accent/20 transition-colors" style={{ height: `${rowHeight}px` }}>
                      <EmployeeDetailsPopover employee={employee} currency={currency}>
                        <div 
                          className="w-48 flex-shrink-0 px-2 border-r border-border bg-card sticky left-0 z-10 flex items-center cursor-pointer hover:bg-accent/30 transition-colors"
                          data-testid={`employee-info-${employee.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            {employee.avatar_url ? (
                              <img 
                                src={employee.avatar_url} 
                                alt={employee.name}
                                className={cn(
                                  "rounded-full object-cover flex-shrink-0 border border-border",
                                  rowHeight <= 48 ? "w-7 h-7" : "w-10 h-10"
                                )}
                              />
                            ) : (
                              <div 
                                className={cn(
                                  "rounded-full flex items-center justify-center font-semibold flex-shrink-0",
                                  rowHeight <= 48 ? "w-7 h-7 text-xs" : "w-10 h-10 text-sm",
                                  !color.isCustom && color.avatar
                                )}
                                style={color.isCustom ? { backgroundColor: employee.color + '20', color: employee.color } : {}}
                              >
                                {employee.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="overflow-hidden min-w-0">
                              <p className="font-medium text-sm truncate leading-tight">{employee.name}</p>
                              {rowHeight > 44 && (
                                <p className="text-xs text-muted-foreground truncate leading-tight">{employee.role}</p>
                              )}
                              {rowHeight > 60 && showCosts && hasCost && (
                                <p className="text-xs text-muted-foreground tabular-nums leading-tight">
                                  {costType === 'paid_leave' ? "(Paid leave)" : costType === 'salary' && employeeShifts.length === 0 ? "(Salary)" : `${dailyHours.toFixed(2)}h`}
                                  <span className="text-primary"> • {currencySymbol}{dailyCost.toFixed(2)}</span>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </EmployeeDetailsPopover>
                      {/* Add left and right margin to match header */}
                      <div 
                        className={cn(
                          "flex-1 relative mx-6 group select-none",
                          absence ? "cursor-not-allowed" : "cursor-crosshair"
                        )}
                        style={{ height: '100%' }}
                        onMouseDown={(e) => !absence && handleDragCreateStart(e, employee, e.currentTarget)}
                        onClick={(e) => handleTimelineClick(e, employee, e.currentTarget)}
                        onMouseMove={(e) => !absence && !dragCreateState && handleTimelineMouseMove(e, employee, e.currentTarget)}
                        onMouseLeave={handleTimelineMouseLeave}
                        data-testid={`timeline-track-${employee.id}`}
                      >
                        {/* Grid lines */}
                        {tickMarks.map((tick, index) => (
                          <div
                            key={index}
                            className={cn(
                              "absolute top-0 bottom-0 pointer-events-none",
                              tick.type === 'hour' && "w-px bg-border/40",
                              tick.type === 'medium' && "w-px bg-border/20",
                              tick.type === 'small' && "w-px bg-border/10"
                            )}
                            style={{ left: `${tick.percent}%` }}
                          />
                        ))}
                        {/* Hover highlight for interval (when not dragging) */}
                        {hoverInfo && hoverInfo.employeeId === employee.id && !dragCreateState && (
                          <div 
                            className={cn(
                              "absolute top-0 bottom-0 pointer-events-none rounded-sm transition-opacity",
                              getColorById(employee.color).bg,
                              "opacity-60"
                            )}
                            style={{ 
                              left: `${hoverInfo.startPercent}%`, 
                              width: `${hoverInfo.endPercent - hoverInfo.startPercent}%` 
                            }}
                          />
                        )}
                        {/* Drag-to-create preview */}
                        {dragCreateState && dragCreateState.employeeId === employee.id && (
                          (() => {
                            const startP = Math.min(dragCreateState.startPercent, dragCreateState.currentPercent);
                            const endP = Math.max(dragCreateState.startPercent, dragCreateState.currentPercent);
                            const width = endP - startP;
                            const startMins = timeToMinutes(dragCreateState.startTime);
                            const endMins = timeToMinutes(dragCreateState.currentTime);
                            const displayStartTime = startMins <= endMins ? dragCreateState.startTime : dragCreateState.currentTime;
                            const displayEndTime = startMins <= endMins ? dragCreateState.currentTime : dragCreateState.startTime;
                            
                            return (
                              <div 
                                className={cn(
                                  "absolute top-1 bottom-1 pointer-events-none rounded-md border-2 border-dashed",
                                  getColorById(employee.color).bg,
                                  getColorById(employee.color).border,
                                  "opacity-80"
                                )}
                                style={{ 
                                  left: `${startP}%`, 
                                  width: `${width}%`,
                                  minWidth: '2px'
                                }}
                                data-testid="drag-create-preview"
                              >
                                {width > 5 && (
                                  <div className="absolute inset-0 flex items-center justify-center text-xs font-medium truncate px-1">
                                    {displayStartTime} - {displayEndTime}
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        )}
                        {dragGuides && (
                          <>
                            <div 
                              className="absolute top-0 bottom-0 w-0.5 bg-primary/60 pointer-events-none z-30"
                              style={{ left: `${dragGuides.startPercent}%`, transform: 'translateX(-50%)' }}
                            />
                            <div 
                              className="absolute top-0 bottom-0 w-0.5 bg-primary/60 pointer-events-none z-30"
                              style={{ left: `${dragGuides.endPercent}%`, transform: 'translateX(-50%)' }}
                            />
                          </>
                        )}
                        {/* Absence Block - covers entire timeline when absent */}
                        {absence && (
                          <div 
                            className="absolute inset-0 bg-muted/80 border border-dashed border-muted-foreground/30 rounded flex items-center justify-center z-5"
                            data-testid={`absence-block-${employee.id}`}
                          >
                            <div className="text-center">
                              <p className="text-sm font-medium text-muted-foreground">
                                {getAbsenceLabel(absence)}
                              </p>
                              {absence.is_paid && (
                                <span className="text-xs text-green-600 dark:text-green-400">(Paid)</span>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Show hint when no shifts and not absent */}
                        {employeeShifts.length === 0 && !hoverInfo && !absence && !dragCreateState && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="flex items-center gap-1 text-muted-foreground/50">
                              <Plus className="w-3 h-3" />
                              <span className="text-xs">Click or drag to add shift</span>
                            </div>
                          </div>
                        )}
                        {employeeShifts.map((shift) => (
                          <ShiftBlock
                            key={shift.id}
                            shift={shift}
                            employee={employee}
                            employeeShifts={employeeShifts}
                            breakTypes={breakTypes}
                            timelineStart={dayHours.open_time}
                            timelineEnd={dayHours.close_time}
                            snapInterval={snapInterval}
                            shiftGapMinutes={shiftGapMinutes}
                            scrollable={false}
                            isLocked={isLocked}
                            onUpdate={(updates) => handleShiftUpdate(shift.id, updates)}
                            onClick={() => handleShiftClick(shift, employee)}
                            onContextEdit={handleContextEdit}
                            onContextCopy={handleContextCopy}
                            onContextDelete={handleContextDelete}
                            onDragGuideUpdate={handleDragGuideUpdate}
                          />
                        ))}
                      </div>
                      {/* Stats Cell */}
                      {showStats && (
                        <div className="w-24 flex-shrink-0 border-l border-border bg-muted/30 flex items-center justify-center">
                          {employeeShifts.length > 0 ? (
                            <div className="space-y-0.5 text-center">
                              <span className="text-xs tabular-nums font-medium">{dailyHours.toFixed(2)}h</span>
                              {showCosts && hasCost && (
                                <p className="text-xs tabular-nums font-medium text-primary">{currencySymbol}{dailyCost.toFixed(2)}</p>
                              )}
                            </div>
                          ) : showCosts && costType === 'paid_leave' ? (
                            <div className="space-y-0.5 text-center">
                              <span className="text-xs tabular-nums text-muted-foreground">(Paid)</span>
                              <p className="text-xs tabular-nums font-medium text-green-600 dark:text-green-400">{currencySymbol}{dailyCost.toFixed(2)}</p>
                            </div>
                          ) : showCosts && costType === 'salary' ? (
                            <div className="space-y-0.5 text-center">
                              <span className="text-xs tabular-nums text-muted-foreground">(Salary)</span>
                              <p className="text-xs tabular-nums font-medium text-primary">{currencySymbol}{dailyCost.toFixed(2)}</p>
                            </div>
                          ) : isOnUnpaidLeave ? (
                            <span className="text-xs text-muted-foreground">(Unpaid)</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

        {/* Edit Dialog */}
        <ShiftEditDialog
          open={isDialogOpen}
          onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) {
              setClickedStartTime(null);
              setClickedEndTime(null);
            }
          }}
          shift={editingShift}
          employee={editingEmployee}
          employeeShifts={editingEmployee ? dayShifts.filter(s => s.employee_id === editingEmployee.id) : []}
          breakTypes={breakTypes}
          shiftPatterns={shiftPatterns}
          onSave={handleSaveShift}
          onDelete={handleDeleteShift}
          businessHours={dayHours}
          snapInterval={snapInterval}
          shiftGapMinutes={shiftGapMinutes}
          currency={currency}
          initialStartTime={clickedStartTime}
          initialEndTime={clickedEndTime}
          isLocked={isLocked}
        />

        {/* Break Required Dialog (for drag-to-create shifts) */}
        <BreakRequiredDialog
          open={isBreakDialogOpen}
          onOpenChange={(open) => {
            setIsBreakDialogOpen(open);
            if (!open) {
              setBreakDialogData(null);
            }
          }}
          employee={breakDialogData?.employee}
          startTime={breakDialogData?.startTime}
          endTime={breakDialogData?.endTime}
          requiredBreaks={breakDialogData?.requiredBreaks || []}
          breakTypes={breakTypes}
          snapInterval={snapInterval}
          onConfirm={handleBreakDialogConfirm}
          onCancel={() => {
            setIsBreakDialogOpen(false);
            setBreakDialogData(null);
          }}
        />

        {/* Copy Shift Dialog */}
        <CopySingleShiftDialog
          open={isCopyDialogOpen}
          onOpenChange={(open) => {
            setIsCopyDialogOpen(open);
            if (!open) {
              setCopyShiftData(null);
            }
          }}
          shift={copyShiftData?.shift}
          sourceEmployee={copyShiftData?.employee}
          employees={employees}
          onCopy={handleCopyShift}
        />

        {/* Delete Shift Confirmation Dialog */}
        <DeleteShiftConfirmDialog
          open={isDeleteDialogOpen}
          onOpenChange={(open) => {
            setIsDeleteDialogOpen(open);
            if (!open) {
              setDeleteShiftData(null);
            }
          }}
          shift={deleteShiftData?.shift}
          employee={deleteShiftData?.employee}
          onConfirm={handleDeleteShiftConfirm}
        />
      </div>
    </DragGuideContext.Provider>
  );
};

export default DayView;
